/**
 * Axes for the law-firm (สำนักงานทนายความ / สำนักงานกฎหมาย) Q&A and keyword
 * clusters.
 *
 * Deterministic and hand-curated. No AI call at build or request time.
 * Content rules: no fees, no outcome guarantees, no unverifiable statistics.
 */
import type { WaveAxis } from "@/data/_qa/wave-expander";
import { PRACTICE_AREAS } from "@/data/practice/areas";

/** Ten practice areas of the firm, reused as the primary topic axis. */
export const LAWFIRM_PRACTICE_AXIS: WaveAxis[] = PRACTICE_AREAS.map((a) => ({
  slug: a.slug,
  th: a.name,
}));

/** Engagement-level topics people search before hiring a firm. */
export const LAWFIRM_ENGAGEMENT_AXIS: readonly WaveAxis[] = [
  { slug: "hire-lawyer", th: "การจ้างทนายความและขอบเขตงานที่ตกลงกัน" },
  { slug: "first-consult", th: "การเข้าปรึกษาทนายครั้งแรก" },
  { slug: "engagement-letter", th: "หนังสือแต่งตั้งทนายและสัญญาจ้างว่าความ" },
  { slug: "power-of-attorney", th: "หนังสือมอบอำนาจให้ทนายดำเนินการ" },
  { slug: "case-status", th: "การติดตามความคืบหน้าของเรื่องและการรายงานลูกความ" },
  { slug: "change-lawyer", th: "การเปลี่ยนทนายหรือถอนทนายกลางทาง" },
  { slug: "conflict-of-interest", th: "การตรวจสอบผลประโยชน์ขัดกันก่อนรับเรื่อง" },
  { slug: "confidentiality", th: "การรักษาความลับของลูกความ" },
  { slug: "lawyer-licence", th: "การตรวจสอบใบอนุญาตทนายความและมรรยาททนายความ" },
  { slug: "document-collection", th: "การรวบรวมและคัดสำเนาเอกสารจากหน่วยงาน" },
  { slug: "translation-certification", th: "การแปลและรับรองเอกสารเพื่อใช้กับศาลและหน่วยงาน" },
  { slug: "deadline-management", th: "การนับกำหนดเวลาตามกฎหมายและวันนัดของหน่วยงาน" },
  { slug: "settlement-vs-court", th: "การเลือกระหว่างเจรจาไกล่เกลี่ยกับการฟ้องคดี" },
  { slug: "cross-border", th: "เรื่องที่มีคู่กรณีหรือทรัพย์สินอยู่ต่างประเทศ" },
  { slug: "one-stop", th: "การรวมงานกฎหมาย บัญชี และเอกสารระหว่างประเทศไว้ในสำนวนเดียว" },
];

/** Forums and agencies the firm files with, as a secondary subject axis. */
export const LAWFIRM_FORUM_AXIS: readonly WaveAxis[] = [
  { slug: "civil-court", th: "ศาลแพ่ง" },
  { slug: "criminal-court", th: "ศาลอาญา" },
  { slug: "labour-court", th: "ศาลแรงงาน" },
  { slug: "tax-court", th: "ศาลภาษีอากร" },
  { slug: "admin-court", th: "ศาลปกครอง" },
  { slug: "ip-trade-court", th: "ศาลทรัพย์สินทางปัญญาและการค้าระหว่างประเทศ" },
  { slug: "juvenile-family-court", th: "ศาลเยาวชนและครอบครัว" },
  { slug: "bankruptcy-court", th: "ศาลล้มละลาย" },
  { slug: "legal-execution", th: "กรมบังคับคดี" },
  { slug: "land-office", th: "สำนักงานที่ดิน" },
  { slug: "dbd", th: "กรมพัฒนาธุรกิจการค้า" },
  { slug: "dip", th: "กรมทรัพย์สินทางปัญญา" },
  { slug: "revenue", th: "กรมสรรพากร" },
  { slug: "sso", th: "สำนักงานประกันสังคม" },
  { slug: "labour-protection", th: "สำนักงานสวัสดิการและคุ้มครองแรงงาน" },
  { slug: "pdpc", th: "สำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล" },
  { slug: "district-office", th: "สำนักงานเขตหรือที่ว่าการอำเภอ" },
  { slug: "arbitration-institute", th: "สถาบันอนุญาโตตุลาการ" },
];
